import { transformKeysToPascalCase } from '@/Utils/ObjectUtility.js';
import {
    orderStages,
    orderPaymentStatuses,
    pledgeSortTypes,
} from '@/Models';

import {
    enumToSelectOptions,
    mapEnumDisplayNameFromResource,
} from '@/Utils/EnumUtility';

import { fieldTypes } from '@/Types/FieldTypes';

import Resources from '@/Resources.js';
import Formatter from '@/Utils/Formatter.js';

export const filterBackerCenterPledgesAndOrdersTypes = {
    createdFrom: 0,
    createdTo: 1,
    hasInstallments: 2,
    isActionRequired: 3,
    maxAmount: 4,
    minAmount: 5,
    paymentStatuses: 6,
    term: 7,
    sortType: 8,
    stages: 9,
    includeArchivedProjects: 10,
};

export function getFilterGroupDisplayName(filterType) {
    const filterGroupDisplayNames = {
        [filterBackerCenterPledgesAndOrdersTypes.createdFrom]: Resources.BackerCenter.PledgeDateFromFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.createdTo]: Resources.BackerCenter.PledgeDateToFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.hasInstallments]: Resources.BackerCenter.InstallmentsFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.isActionRequired]: Resources.BackerCenter.ActionRequiredFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.maxAmount]: Resources.BackerCenter.PledgeValueToFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.minAmount]: Resources.BackerCenter.PledgeValueFromFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.paymentStatuses]: Resources.BackerCenter.PaymentStatusFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.term]: Resources.BackerCenter.SearchPhraseFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.sortType]: Resources.Search.SearchProjectSortGroupLabel,
        [filterBackerCenterPledgesAndOrdersTypes.stages]: Resources.BackerCenter.PledgeStageFilterLabel,
        [filterBackerCenterPledgesAndOrdersTypes.includeArchivedProjects]: Resources.BackerCenter.ArchivedProjectsFilterLabel,
    };
    return filterGroupDisplayNames[filterType];
}

export function buildDefaultPledgesAndOrdersFilterParams(params = {}) {
    const formattedParams = transformKeysToPascalCase(params);
    return {
        createdFrom: formattedParams.createdFrom ? new Date(formattedParams.createdFrom) : null,
        createdTo: formattedParams.createdTo ? new Date(formattedParams.createdTo) : null,
        hasInstallments: !!formattedParams.hasInstallments || null,
        isActionRequired: !!formattedParams.isActionRequired || null,
        maxAmount: formattedParams.maxAmount ?? null,
        minAmount: formattedParams.minAmount ?? null,
        paymentStatuses: formattedParams.paymentStatuses || [],
        term: formattedParams.term || '',
        sortType: formattedParams.sortType || 0,
        stages: formattedParams.stages || [],
        includeArchivedProjects: formattedParams.includeArchivedProjects ?? null,
    };
}

export function buildPledgesAndOrdersCurrentFilter(searchParams) {
    const staticFilters = [
        {
            hasValue: searchParams.createdFrom !== null,
            key: 'createdFrom',
            group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.createdFrom),
            label: Formatter.format(searchParams.createdFrom, 'yyyy-MM-dd'),
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.createdFrom,
        },
        {
            hasValue: searchParams.createdTo !== null,
            key: 'createdTo',
            group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.createdTo),
            label: Formatter.format(searchParams.createdTo, 'yyyy-MM-dd'),
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.createdTo,
        },
        {
            hasValue: searchParams.hasInstallments === true,
            key: 'hasInstallments',
            label: Resources.BackerCenter.ShowPledgesWithInstallmentsOnlyLabel,
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.hasInstallments,
        },
        {
            hasValue: searchParams.isActionRequired === true,
            key: 'isActionRequired',
            label: Resources.BackerCenter.ShowPledgesWithActionRequiredOnlyLabel,
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.isActionRequired,
        },
        {
            hasValue: searchParams.maxAmount !== null,
            key: 'maxAmount',
            group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.maxAmount),
            label: searchParams.maxAmount,
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.maxAmount,
        },
        {
            hasValue: searchParams.minAmount !== null,
            key: 'minAmount',
            group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.minAmount),
            label: searchParams.minAmount,
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.minAmount,
        },
        {
            hasValue: !!searchParams.term,
            key: 'term',
            group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.term),
            label: searchParams.term,
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.term,
        },
        {
            hasValue: searchParams.includeArchivedProjects === true,
            key: 'includeArchivedProjects',
            label: Resources.BackerCenter.IncludeArchivedProjectsLabel,
            filterSearchType: filterBackerCenterPledgesAndOrdersTypes.includeArchivedProjects,
        },
    ];
    const paymentStatusFilters = (searchParams.paymentStatuses || []).map(paymentStatus => ({
        hasValue: true,
        key: orderPaymentStatuses[paymentStatus].name,
        label: orderPaymentStatuses[paymentStatus].displayName,
        group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.paymentStatuses),
        filterSearchType: filterBackerCenterPledgesAndOrdersTypes.paymentStatuses,
        value: paymentStatus,
    }));

    const stageFilters = (searchParams.stages || []).map(stage => ({
        hasValue: true,
        key: orderStages[stage].name,
        label: orderStages[stage].displayName,
        group: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.stages),
        filterSearchType: filterBackerCenterPledgesAndOrdersTypes.stages,
        value: stage,
    }));

    return [
        ...staticFilters,
        ...paymentStatusFilters,
        ...stageFilters,
    ].filter(f => f.hasValue);
}

export function removeSearchSelectedFilter(filter, searchParams) {
    const result = { ...searchParams };
    const filterValue = v => v !== filter.value;

    const strategies = {
        [filterBackerCenterPledgesAndOrdersTypes.minAmount]: () => {
            result.minAmount = null;
        },
        [filterBackerCenterPledgesAndOrdersTypes.maxAmount]: () => {
            result.maxAmount = null;
        },
        [filterBackerCenterPledgesAndOrdersTypes.createdFrom]: () => {
            result.createdFrom = null;
        },
        [filterBackerCenterPledgesAndOrdersTypes.createdTo]: () => {
            result.createdTo = null;
        },
        [filterBackerCenterPledgesAndOrdersTypes.hasInstallments]: () => {
            result.hasInstallments = null;
        },
        [filterBackerCenterPledgesAndOrdersTypes.isActionRequired]: () => {
            result.isActionRequired = null;
        },
        [filterBackerCenterPledgesAndOrdersTypes.paymentStatuses]: () => {
            result.paymentStatuses = (searchParams.paymentStatuses || []).filter(filterValue);
        },
        [filterBackerCenterPledgesAndOrdersTypes.term]: () => {
            result.term = '';
        },
        [filterBackerCenterPledgesAndOrdersTypes.sortType]: () => {
            result.sortType = 0;
        },
        [filterBackerCenterPledgesAndOrdersTypes.stages]: () => {
            result.stages = (searchParams.stages || []).filter(filterValue);
        },
        [filterBackerCenterPledgesAndOrdersTypes.includeArchivedProjects]: () => {
            result.includeArchivedProjects = null;
        },
    };

    const strategy = strategies[filter.filterSearchType];
    if (strategy) {
        strategy();
    }

    return result;
}

export function buildPledgesAndOrdersSortList() {
    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'sortType',
            label: Resources.Search.SearchProjectSortGroupLabel,
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.Common, pledgeSortTypes),
            ),
            props: {
                type: 'radio',
                showLabel: false,
            },
        },
    ];
}

export function buildPledgesAndOrdersFilterList() {
    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'stages',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.stages),
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.OrderStatus, orderStages),
                e => e.value !== orderStages.unfinished && e.value !== orderStages.editedByCreator,
            ),
        },
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'paymentStatuses',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.paymentStatuses),
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.OrderStatus, orderPaymentStatuses),
            ),
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'isActionRequired',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.isActionRequired),
            props: {
                checkedValue: true,
                customLabelText: Resources.BackerCenter.ShowPledgesWithActionRequiredOnlyLabel,
                uncheckedValue: null,
            },
        },
        {
            type: fieldTypes.text,
            modelName: 'term',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.term),
            rules: { max: 80 },
            placeholder: Resources.BackerCenter.SearchPhrasePlaceholderLabel,
            qa: 'searchTerm',
        },
        {
            type: fieldTypes.date,
            modelName: 'createdFrom',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.createdFrom),
            props: {
                valueType: 'date',
            },
        },
        {
            type: fieldTypes.date,
            modelName: 'createdTo',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.createdTo),
            props: {
                valueType: 'date',
            },
        },
        {
            type: fieldTypes.number,
            modelName: 'minAmount',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.minAmount),
            rules: { max: 10 },
            props: {
                textAlign: 'left',
            },
        },
        {
            type: fieldTypes.number,
            modelName: 'maxAmount',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.maxAmount),
            rules: { max: 10 },
            props: {
                textAlign: 'left',
            },
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'hasInstallments',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.hasInstallments),
            props: {
                checkedValue: true,
                customLabelText: Resources.BackerCenter.ShowPledgesWithInstallmentsOnlyLabel,
            },
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'includeArchivedProjects',
            label: getFilterGroupDisplayName(filterBackerCenterPledgesAndOrdersTypes.includeArchivedProjects),
            props: {
                customLabelText: Resources.BackerCenter.IncludeArchivedProjectsLabel,
            },
            qa: 'includeArchivedProjects',
        },
    ];
}